import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, PackageSearch, AlertTriangle, RotateCw, Clock } from "lucide-react";
import { BrandHeader } from "@/components/rastreo/BrandHeader";
import { Timeline } from "@/components/rastreo/Timeline";
import { Gallery } from "@/components/rastreo/Gallery";
import { StageBadge } from "@/components/rastreo/StageBadge";
import { UnidadDetalleSkeleton } from "@/components/rastreo/UnidadDetalleSkeleton";
import { TipoIcon } from "@/components/rastreo/TipoIcon";
import { VideosEnlace } from "@/components/rastreo/VideosEnlace";
import { obtenerUnidad, obtenerEnlace, clasificarError, mensajeError, api } from "@/lib/api";
import { formatFecha, nombreTipo, iconoTipo } from "@/lib/format";
import { RASTREO } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";

export default function Detalle() {
  const { vin } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [estado, setEstado] = useState("cargando"); // cargando | ok | no-encontrado | error-servicio | limite
  const [msgLimite, setMsgLimite] = useState("");
  const [intento, setIntento] = useState(0);
  const [tipos, setTipos] = useState([]);
  const [params] = useSearchParams();
  const tokenEnlace = params.get("enlace");
  const [enlaceInfo, setEnlaceInfo] = useState(null);
  const [enlaceError, setEnlaceError] = useState("");

  const cargarEnlace = () =>
    obtenerEnlace(tokenEnlace)
      .then((i) => { setEnlaceInfo(i); setEnlaceError(""); })
      .catch((err) => setEnlaceError(mensajeError(err, "No pudimos validar el enlace. Intenta de nuevo en un momento.")));

  useEffect(() => {
    if (tokenEnlace) cargarEnlace();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenEnlace]);

  usePageTitle(data ? `${data.unidad.marca} ${data.unidad.modelo} · ${data.unidad.vin}` : "Buscando unidad");

  useEffect(() => {
    api.get("/tipos").then((r) => setTipos(r.data)).catch(() => {});
  }, []);


  useEffect(() => {
    let activo = true;
    setEstado("cargando");
    obtenerUnidad(vin)
      .then((d) => { if (activo) { setData(d); setEstado("ok"); } })
      .catch((err) => {
        if (!activo) return;
        // Un 500/timeout no significa que el VIN no exista, y un 429 (límite de
        // consultas) tampoco: cada caso muestra su propio mensaje.
        const tipo = clasificarError(err);
        if (tipo === "limite") {
          setMsgLimite(mensajeError(err, "Estás realizando muchas consultas. Esperá unos minutos e intentá de nuevo."));
          setEstado("limite");
        } else if (tipo === "servicio") {
          setEstado("error-servicio");
        } else {
          setEstado("no-encontrado");
        }
      });
    return () => { activo = false; };
  }, [vin, intento]);

  if (estado === "cargando") {
    return (
      <div className="min-h-screen bg-[var(--tc-surface)]">
        <BrandHeader compact />
        <main className="mx-auto max-w-5xl px-5 py-6 sm:py-8">
          <UnidadDetalleSkeleton />
        </main>
      </div>
    );
  }

  if (estado === "no-encontrado") {
    return (
      <div className="min-h-screen bg-[var(--tc-surface)]">
        <BrandHeader />
        <main className="mx-auto max-w-2xl px-5 py-20">
          <div
            data-testid={RASTREO.notFound}
            className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-8 text-center"
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]">
              <PackageSearch size={26} />
            </span>
            <h2 className="mt-4 text-lg font-semibold text-[var(--tc-text)]">
              No encontramos esa unidad
            </h2>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-[var(--tc-text-secondary)]">
              Revisá el número de serie e intentá de nuevo.
            </p>
            <button
              data-testid={RASTREO.notFoundBack}
              onClick={() => navigate("/")}
              className="mt-6 inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)]"
            >
              <ArrowLeft size={16} /> Volver a buscar
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (estado === "limite") {
    return (
      <div className="min-h-screen bg-[var(--tc-surface)]">
        <BrandHeader />
        <main className="mx-auto max-w-2xl px-5 py-20">
          <div
            data-testid={RASTREO.rateLimitError}
            className="rounded-[var(--tc-radius)] border border-amber-200 bg-amber-50 p-8 text-center"
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white text-amber-600">
              <Clock size={26} />
            </span>
            <h2 className="mt-4 text-lg font-semibold text-[var(--tc-text)]">
              Estás consultando muy rápido
            </h2>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-[var(--tc-text-secondary)]">
              {msgLimite}
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (estado === "error-servicio") {
    return (
      <div className="min-h-screen bg-[var(--tc-surface)]">
        <BrandHeader />
        <main className="mx-auto max-w-2xl px-5 py-20">
          <div
            data-testid={RASTREO.serviceError}
            className="rounded-[var(--tc-radius)] border border-red-200 bg-red-50 p-8 text-center"
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white text-red-500">
              <AlertTriangle size={26} />
            </span>
            <h2 className="mt-4 text-lg font-semibold text-[var(--tc-text)]">
              Hubo un problema para cargar esta unidad
            </h2>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-[var(--tc-text-secondary)]">
              No es que tu unidad no exista: el servicio tuvo un problema. Intentá de nuevo en un momento.
            </p>
            <button
              data-testid={RASTREO.serviceErrorRetry}
              onClick={() => setIntento((i) => i + 1)}
              className="mt-6 inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--tc-accent-hover)]"
            >
              <RotateCw size={16} /> Reintentar
            </button>
          </div>
        </main>
      </div>
    );
  }

  const { unidad, cliente, etapa_actual, timeline, fotos } = data;

  return (
    <div className="min-h-screen bg-[var(--tc-surface)]">
      <BrandHeader compact />

      <main className="mx-auto max-w-5xl px-5 py-6 sm:py-8">
        <Link
          to="/"
          data-testid={RASTREO.backHome}
          className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-[var(--tc-text-secondary)] transition-colors hover:text-[var(--tc-accent)]"
        >
          <ArrowLeft size={16} /> Nueva búsqueda
        </Link>

        {/* Encabezado */}
        <div
          data-testid={RASTREO.detailHeader}
          className="rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6 tc-fade-up"
        >
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[var(--tc-radius)] bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]">
                <TipoIcon icono={iconoTipo(tipos, unidad.tipo)} size={24} />
              </span>
              <div>
                <h1 className="text-xl font-semibold tracking-tight text-[var(--tc-text)] sm:text-2xl">
                  {unidad.marca} {unidad.modelo}
                </h1>
                <p
                  data-testid={RASTREO.detailVin}
                  className="mt-1 font-mono text-sm tabular-nums text-[var(--tc-text-secondary)]"
                >
                  {unidad.vin}
                </p>
                {cliente && (
                  <p className="mt-1 text-xs text-[var(--tc-text-secondary)]">{cliente.nombre}</p>
                )}
              </div>
            </div>
            <StageBadge etapa={etapa_actual} />
          </div>
        </div>

        {/* Timeline */}
        <Panel titulo="Estado del viaje" className="mt-5">
          <div className="pt-2">
            <Timeline timeline={timeline} />
          </div>
        </Panel>

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          {/* Ficha del vehículo */}
          <Panel titulo="Ficha del vehículo" testId={RASTREO.vehicleCard}>
            <Datos
              filas={[
                ["Marca", unidad.marca],
                ["Modelo", unidad.modelo],
                ["Año", unidad.anio],
                ["Color", unidad.color],
                ["Tipo", nombreTipo(tipos, unidad.tipo)],
                ["VIN", unidad.vin, true],
              ]}
            />
          </Panel>

          {/* Datos del envío */}
          <Panel titulo="Datos del envío" testId={RASTREO.shippingCard}>
            <Datos
              filas={[
                ["Origen", unidad.datos_envio.origen],
                ["Destino", unidad.datos_envio.destino],
                ["Puerto de origen", unidad.datos_envio.puerto_origen],
                ["Puerto de destino", unidad.datos_envio.puerto_destino],
                ["Buque", unidad.datos_envio.buque],
                ["Entrega estimada", formatFecha(unidad.datos_envio.fecha_estimada_entrega)],
              ]}
            />
          </Panel>
        </div>

        {/* Videos del destinatario: solo con un enlace de unidad válido para ESTE vehículo */}
        {tokenEnlace && enlaceError && (
          <p data-testid={RASTREO.videoLinkInvalid} role="alert" className="mt-5 rounded-[var(--tc-radius)] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            {enlaceError}
          </p>
        )}
        {tokenEnlace && enlaceInfo && enlaceInfo.unidad.vin === unidad.vin && (
          <VideosEnlace token={tokenEnlace} info={enlaceInfo} onChange={cargarEnlace} />
        )}

        {/* Galería */}
        {fotos && fotos.length > 0 && (
          <Panel titulo="Registro fotográfico" className="mt-5">
            <div className="pt-2">
              <Gallery fotos={fotos} />
            </div>
          </Panel>
        )}
      </main>

      <footer className="mx-auto max-w-5xl px-5 py-10 text-center text-xs text-[var(--tc-text-secondary)]">
        LOCCITEC · Torre de Control
      </footer>
    </div>
  );
}

function Panel({ titulo, children, className = "", testId }) {
  return (
    <section
      data-testid={testId}
      className={`rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white p-6 tc-fade-up ${className}`}
    >
      <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-[var(--tc-text-secondary)]">
        {titulo}
      </h2>
      {children}
    </section>
  );
}

function Datos({ filas }) {
  return (
    <dl className="mt-3 divide-y divide-[var(--tc-border)]">
      {filas.map(([k, v, mono]) => (
        <div key={k} className="flex items-center justify-between gap-4 py-2.5">
          <dt className="text-sm text-[var(--tc-text-secondary)]">{k}</dt>
          <dd
            className={`text-right text-sm font-medium text-[var(--tc-text)] ${mono ? "font-mono text-xs tabular-nums" : "tabular-nums"}`}
          >
            {v || "—"}
          </dd>
        </div>
      ))}
    </dl>
  );
}
